import {
  IconChevronDown,
  IconChevronUp,
  IconDeviceMobile,
  IconLogout,
  IconSettings,
  IconUserCircle,
} from '@tabler/icons-react'
import { Avatar, Badge, Menu, UnstyledButton } from '@mantine/core'
import { useLocation, Link, NavLink, useNavigate, Outlet } from 'react-router-dom'
import { useState, useEffect, type MouseEvent } from 'react'
import { useMedplum } from '@medplum/react'
import { formatHumanName } from '@medplum/core'
import { useSession } from './session'
import logoSvg from '/logo.svg'
import '@mantine/core/styles.css'
import '@mantine/notifications/styles.css'
import { PractitionerRole } from '@medplum/fhirtypes'
import { SNOMED_USER_ROLES } from '@/utils'
import { cn } from '@/utils/cn'
import { useMessagesUnread } from '@/pages/Messages/useMessagesUnread'
import { usePopulationHealthAccess } from '@/pages/Population/usePopulationHealthAccess'
import { useCrmAccess } from '@/pages/Crm'
import { useUnreadInboxFaxes } from '@/hooks/useUnreadInboxFaxes'
import { useSectionNavPath } from '@/hooks/useSectionNavPath'
import { clearSessionState } from '@/hooks/useSessionState'
import { useRecentPatients } from '@/hooks/useRecentPatients'
import { usePractitionerOpsScope } from '@/hooks/usePractitionerOpsScope'
import { useMyProfileIfLoaded } from '@/hooks/useMyProfile'
import { useBinaryObjectUrl } from '@/hooks/useBinaryObjectUrl'
import { GlobalPatientSearch } from './GlobalPatientSearch'
import { RecentPatientsBar } from './RecentPatientsBar'
import { NotificationBell } from './NotificationBell'

/** BeHuman Pro, the provider iOS app (PD-1934). */
const PROVIDER_APP_URL = 'https://apps.apple.com/app/behuman-pro/id6802459131'

// Shared classes for every top-bar link. Inactive labels are medium weight in a
// soft neutral; the selected one is semibold blue and carries the underline
// indicator so the state never rests on colour alone.
const navLinkClass =
  'relative flex h-[64px] cursor-pointer items-center px-3 text-sm font-medium whitespace-nowrap text-slate-600 transition-colors outline-none hover:text-primary focus-visible:outline-2 focus-visible:-outline-offset-4 focus-visible:outline-primary xl:px-4'
const navLinkActiveClass = 'text-primary font-semibold'
const navIndicatorClass = 'bg-primary absolute inset-x-2 bottom-0 h-0.5 rounded-full xl:inset-x-3'

const hospitalStuffPages = [
  { path: '/', label: 'Home' },
  { path: '/schedule', label: 'Schedule' },
  { path: '/patients', label: 'Patients' },
  { path: '/appointments', label: 'Care Coordination' },
  { path: '/billing', label: 'Billing' },
  { path: '/fax', label: 'eFax' },
  { path: '/messages', label: 'Messages' },
  { path: '/assistant', label: 'BeAssistant' },
]
const callCenterPages = [{ path: '/', label: 'Home' }]

// Provider Operations sits after Patients, for callers whose scope is non-empty (Care
// Coordinators and super admins). The middleware decides that, not a role lookup here.
const providerOperationsPage = { path: '/providers', label: 'Provider Operations' }
const hospitalStuffPagesWithProviderOperations = hospitalStuffPages.flatMap((page) =>
  page.path === '/patients' ? [page, providerOperationsPage] : [page],
)

// Clicking a tab you are already inside resets that section (PD-1206). /fax,
// /patients and /appointments reset by route, because they have somewhere to go.
const sectionResetPaths: Record<string, string> = {
  '/patients': '/patients',
  '/fax': '/fax',
  '/appointments': '/appointments?tab=care-queue',
}

// A single-route section has no sub-route to pop, so its reset is to drop the
// in-page state it remembers — each section owns a key namespace in the session
// store, and the route element is remounted so the page re-reads the defaults.
// Messages names one key rather than the whole prefix: the per-patient entries
// under it belong to the patient chart, not to this tab.
const sectionStateKeys: Record<string, string> = {
  '/': 'home:',
  '/schedule': 'schedule:',
  '/billing': 'billing:selectedPatient',
  '/messages': 'messages:selectedThread:all',
  '/assistant': 'assistant:',
}
const superAdminMenu = [{ path: '/super', label: 'Super Admin' }]
// Shown to whoever the populationHealth LaunchDarkly flag targets, whatever their role.
const populationHealthPage = { path: '/population', label: 'Population' }
// PD-1993: for super admins and care coordinators.
const crmPage = { path: '/crm', label: 'CRM' }

export const Shell = () => {
  const location = useLocation()
  const navigate = useNavigate()
  const medplum = useMedplum()

  const session = useSession()
  const populationHealthAccess = usePopulationHealthAccess()
  const crmAccess = useCrmAccess()
  const { hasUnreadMessages } = useMessagesUnread()
  const { data: hasUnreadFaxes } = useUnreadInboxFaxes()
  const { recordPatient } = useRecentPatients()
  const { organizations: practitionerOpsOrganizations } = usePractitionerOpsScope()
  const staffPages =
    practitionerOpsOrganizations.length > 0
      ? hospitalStuffPagesWithProviderOperations
      : hospitalStuffPages

  // A nav tab reopens the page the user last had open in that section — the fax
  // they were reading, the patient chart they were in. While already inside a
  // section, its anchor keeps the exact current location so native modified and
  // non-primary clicks open the page the href describes. Plain active-tab reset
  // clicks are handled separately below (PD-1206).
  const navPaths: Record<string, string> = {
    '/fax': useSectionNavPath('/fax'),
    '/patients': useSectionNavPath('/patients'),
    '/appointments':
      location.pathname === '/appointments' || location.pathname.startsWith('/appointments/')
        ? `${location.pathname}${location.search}`
        : '/appointments',
    '/billing':
      location.pathname === '/billing' || location.pathname.startsWith('/billing/')
        ? `${location.pathname}${location.search}`
        : '/billing',
    '/assistant':
      location.pathname === '/assistant' || location.pathname.startsWith('/assistant/')
        ? `${location.pathname}${location.search}`
        : '/assistant',
  }

  // A reset-marked navigation must reach the route before its element remounts.
  // AssistantPage uses that marker to suppress stale write-back from the old
  // instance, then the new instance reads the cleared store and stripped URL.
  const locationSectionResetKey = (location.state as { sectionResetKey?: unknown } | null)
    ?.sectionResetKey
  const requestedSectionResetKey =
    typeof locationSectionResetKey === 'number' &&
    Number.isSafeInteger(locationSectionResetKey) &&
    locationSectionResetKey >= 0
      ? locationSectionResetKey
      : undefined
  const [sectionResetKey, setSectionResetKey] = useState(() => requestedSectionResetKey ?? 0)
  useEffect(() => {
    if (requestedSectionResetKey === undefined || requestedSectionResetKey <= sectionResetKey) {
      return
    }
    setSectionResetKey(requestedSectionResetKey)
  }, [requestedSectionResetKey, sectionResetKey])

  const resetSectionIfInside = (event: MouseEvent<HTMLAnchorElement>, path: string) => {
    if (event.button !== 0 || event.metaKey || event.ctrlKey || event.altKey || event.shiftKey) {
      return
    }

    const currentPath = `${location.pathname}${location.search}`
    const resetPath = sectionResetPaths[path]
    const inSection = location.pathname === path || location.pathname.startsWith(`${path}/`)
    if (resetPath && inSection) {
      event.preventDefault()
      void navigate(resetPath, { replace: resetPath === currentPath })
      return
    }

    const prefix = sectionStateKeys[path]
    if (!prefix || location.pathname !== path) return
    event.preventDefault()
    const resetKey = sectionResetKey + 1
    clearSessionState(prefix)
    void navigate(path, {
      replace: path === currentPath,
      state: { sectionResetKey: resetKey },
    })
  }

  const [isMenuOpen, setIsMenuOpen] = useState<boolean>(false)
  const [userRole, setUserRole] = useState<PractitionerRole | null>(null)
  const [isCheckingRole, setIsCheckingRole] = useState(false)
  const profile = medplum.getProfile()

  // The session profile Medplum caches only refreshes on sign-in, so once My Profile has
  // been read the header follows that read instead: a save there refetches it, and the new
  // name and photo show here at once. Its photo is a `Binary/<id>` the viewer downloads;
  // while that download is in flight the session photo stands in, so a mount never flashes
  // initials. Once the read says there is no photo, initials it is.
  const myProfile = useMyProfileIfLoaded()
  const myPhotoUrl = useBinaryObjectUrl(myProfile?.photo?.url)
  const sessionPhotoUrl = profile?.photo?.[0]?.url
  const photoUrl = !myProfile
    ? sessionPhotoUrl
    : myProfile.photo
      ? (myPhotoUrl ?? sessionPhotoUrl)
      : undefined

  useEffect(() => {
    if (!profile || userRole) return
    const fetchRole = async () => {
      setIsCheckingRole(true)
      try {
        const roleRes = await medplum.searchOne(
          'PractitionerRole',
          `practitioner=Practitioner/${profile.id}`,
        )
        if (roleRes?.code && roleRes?.code.length > 0) {
          setUserRole(roleRes as PractitionerRole)
        }
      } catch (error) {
        console.error('[Shell] Failed to fetch practitioner role:', error)
      } finally {
        setIsCheckingRole(false)
      }
    }
    fetchRole()
  }, [profile, userRole, medplum])

  if (!profile) {
    return null
  }

  const inNewTab = !!location.pathname.match(/\/call\//i)

  const onNavigate = (path: string) => {
    if (inNewTab) {
      window.open(path, '_blank', 'noopener,noreferrer')
      return
    }

    navigate(path)
  }

  const handlePatientPicked = (patient: { id: string; name: string }) => {
    recordPatient(patient)
    if (inNewTab) {
      window.open(`/patients/${patient.id}`, '_blank', 'noopener,noreferrer')
      return
    }
    navigate(`/patients/${patient.id}`)
  }

  const userName = myProfile?.displayName ?? formatHumanName(profile.name?.[0])
  // `PractitionerRole.code[0].coding[0].display` is what InviteCallCenterAgent
  // writes; fall back to the CodeableConcept text, and render nothing if neither
  // is present rather than showing a raw SNOMED code.
  const roleLabel = userRole?.code?.[0]?.coding?.[0]?.display ?? userRole?.code?.[0]?.text

  function renderMenuByRole() {
    let menuItemsByRole = []
    if (isCheckingRole) {
      return null
    }

    const populationMenu = [
      ...(populationHealthAccess ? [populationHealthPage] : []),
      ...(crmAccess ? [crmPage] : []),
    ]
    if (medplum.isSuperAdmin()) {
      menuItemsByRole = [...staffPages, ...populationMenu, ...superAdminMenu]
    } else if (
      userRole &&
      userRole.code &&
      userRole?.code.length > 0 &&
      userRole?.code?.some(
        (item) => item.coding && item.coding[0].code === SNOMED_USER_ROLES.CC_MANAGER.code,
      )
    ) {
      menuItemsByRole = [...callCenterPages, ...populationMenu]
    } else {
      menuItemsByRole = [...staffPages, ...populationMenu]
    }

    return menuItemsByRole.map((item) => {
      const hasUnread =
        (item.path === '/messages' && hasUnreadMessages) || (item.path === '/fax' && hasUnreadFaxes)

      return (
        <NavLink
          key={item.path}
          to={navPaths[item.path] ?? item.path}
          onClick={(event) => resetSectionIfInside(event, item.path)}
          target={inNewTab ? '_blank' : '_self'}
          className={({ isActive }) => cn([navLinkClass, isActive && navLinkActiveClass])}
        >
          {({ isActive }) => (
            <>
              <span className="relative">
                {item.label}
                {hasUnread && (
                  <span className="absolute -top-0.5 -right-2.5 h-2 w-2 rounded-full bg-blue-500" />
                )}
              </span>
              {isActive && <span className={navIndicatorClass} />}
            </>
          )}
        </NavLink>
      )
    })
  }
  return (
    <div>
      <header className="fixed top-0 z-100 flex h-[64px] w-full items-center border-b border-b-[#EEEEEE] bg-white px-6 shadow-xs">
        {/* Logo */}
        <Link to="/" target={inNewTab ? '_blank' : '_self'}>
          <img src={logoSvg} alt="behuman" className="w-[95px]" />
        </Link>

        {/* Nav */}
        <nav className="ml-6 flex shrink-0 xl:ml-10">{profile && renderMenuByRole()}</nav>

        {/* Spacer */}
        <div className="flex-1" />

        {/* Global patient search — grouped with the right-hand controls.
            Hidden below xl: at narrower desktop widths the eight nav labels plus
            the right-hand controls leave no room for it without crowding. */}
        <div className="mr-3 hidden xl:block">
          <GlobalPatientSearch
            placeholder="Search patients..."
            onPick={(patient) => handlePatientPicked(patient)}
          />
        </div>

        {/* Task-assignment notifications, plus a prescriber's DoseSpot counts */}
        <div className="mr-3">
          <NotificationBell />
        </div>

        {/* User menu */}
        <Menu
          withArrow
          width={280}
          position="bottom-end"
          radius="lg"
          shadow="md"
          // Items get their own smaller radius and the dropdown a little inner
          // padding, so a hovered item's background can't square off the
          // dropdown's rounded corners.
          classNames={{ dropdown: 'p-1.5', item: 'rounded-lg' }}
          onChange={setIsMenuOpen}
        >
          <Menu.Target>
            <UnstyledButton
              className={cn([
                // Border stays in the box model as transparent so revealing it
                // on hover/open can't nudge the toolbar layout.
                'focus-visible:outline-primary flex shrink-0 items-center gap-2 rounded-2xl border border-transparent py-1.5 pr-2.5 pl-1.5 transition-colors outline-none hover:border-slate-200 hover:bg-slate-50 focus-visible:outline-2',
                isMenuOpen && 'border-slate-200 bg-slate-50',
              ])}
            >
              <Avatar
                src={photoUrl}
                name={userName}
                color="#275587"
                size={36}
                className="border border-[#D0E1F2]"
              />
              <div className="hidden min-w-0 text-left lg:block">
                <div className="truncate text-sm font-semibold text-slate-900">{userName}</div>
                {roleLabel && <div className="truncate text-xs text-slate-500">{roleLabel}</div>}
              </div>
              {!isMenuOpen && <IconChevronDown width={15} />}
              {isMenuOpen && <IconChevronUp width={15} />}
            </UnstyledButton>
          </Menu.Target>
          <Menu.Dropdown>
            {/* Identity header — repeats who is signed in, so the menu is
                self-describing even at widths where the trigger hides the name. */}
            <div className="flex items-center gap-3 px-2 py-2">
              <Avatar
                src={photoUrl}
                name={userName}
                color="#275587"
                size={44}
                className="border border-[#D0E1F2]"
              />
              <div className="min-w-0">
                <div className="truncate text-sm font-semibold text-slate-900">{userName}</div>
                {roleLabel && <div className="truncate text-xs text-slate-500">{roleLabel}</div>}
              </div>
            </div>
            <Menu.Divider />
            {/* My Profile owns the photo now (docs/GLOSSARY.md "My Profile"). */}
            <Menu.Item
              leftSection={<IconUserCircle size={16} />}
              onClick={() => onNavigate('/profile')}
            >
              View profile
            </Menu.Item>
            <Menu.Item
              leftSection={<IconSettings size={16} />}
              onClick={() => onNavigate('/settings/terminology')}
            >
              Settings
            </Menu.Item>
            <Menu.Item
              component="a"
              href={PROVIDER_APP_URL}
              target="_blank"
              rel="noopener noreferrer"
              leftSection={<IconDeviceMobile size={16} />}
              rightSection={
                <Badge size="xs" variant="light">
                  NEW
                </Badge>
              }
            >
              Get provider mobile app
            </Menu.Item>
            <Menu.Divider />
            <Menu.Item color="red" leftSection={<IconLogout size={16} />} onClick={session.logout}>
              Log out
            </Menu.Item>
          </Menu.Dropdown>
        </Menu>
      </header>

      {/* RECENT chip + patient pill row */}
      <RecentPatientsBar />

      <main
        style={{
          // Reserve space when <ZoomPanel> is mounted. The panel publishes
          // its current width onto --zoom-panel-width (see ZoomPanel.tsx)
          // and clears it on unmount, so non-call pages get the full
          // viewport. Updates on drag-resize automatically reflow content.
          paddingRight: 'var(--zoom-panel-width, 0px)',
          // Reserve space for <RecentPatientsBar> when it has at least one
          // pill to render (the bar publishes its height onto --recent-bar-height).
          paddingTop: `calc(64px + var(--recent-bar-height, 0px))`,
          transition: 'padding-right 0s',
        }}
      >
        <div
          className="relative flex flex-col"
          style={{
            minHeight: `calc(100vh - 64px - var(--recent-bar-height, 0px))`,
            paddingTop: `var(--patient-header-height, 0px)`,
          }}
        >
          <Outlet key={sectionResetKey} />
        </div>
      </main>
    </div>
  )
}
